// Theme picker — light / dark (grey or OLED) / system.
//
// The actual color swap is driven by CSS custom-property overrides in
// app.css keyed off `data-theme` on <html>. An inline script in app.html
// applies the resolved theme before first paint to avoid a flash.

export type ThemeChoice = 'system' | 'light' | 'dark' | 'oled';

const STORAGE_KEY = 'vibe-theme';

function resolveDataAttr(choice: ThemeChoice, prefersDark: boolean): string {
  if (choice === 'system') return prefersDark ? 'dark' : 'light';
  return choice;
}

function readStored(): ThemeChoice {
  if (typeof localStorage === 'undefined') return 'system';
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' || v === 'oled' ? v : 'system';
}

class ThemeState {
  choice = $state<ThemeChoice>('system');
  private mql: MediaQueryList | null = null;
  private onSystemChange = (): void => {
    if (this.choice === 'system') this.apply();
  };

  init(): void {
    if (typeof window === 'undefined') return;
    this.choice = readStored();
    this.mql = window.matchMedia('(prefers-color-scheme: dark)');
    this.mql.addEventListener('change', this.onSystemChange);
    this.apply();
  }

  set(choice: ThemeChoice): void {
    this.choice = choice;
    if (choice === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, choice);
    this.apply();
  }

  /** True when the active dark variant (either explicit or via system) is on. */
  get isDarkActive(): boolean {
    if (this.choice === 'dark' || this.choice === 'oled') return true;
    if (this.choice === 'system') return this.mql?.matches ?? false;
    return false;
  }

  private transitionTimer: ReturnType<typeof setTimeout> | null = null;

  private apply(): void {
    const prefersDark = this.mql?.matches ?? false;
    const attr = resolveDataAttr(this.choice, prefersDark);
    const html = document.documentElement;
    // Enable cross-fade only during the swap. Removing the class after
    // the transition window prevents perma-laggy hovers across the app.
    html.classList.add('theme-transitioning');
    html.setAttribute('data-theme', attr);
    if (this.transitionTimer) clearTimeout(this.transitionTimer);
    this.transitionTimer = setTimeout(() => {
      html.classList.remove('theme-transitioning');
      this.transitionTimer = null;
    }, 250);
  }
}

export const theme = new ThemeState();
